import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Battery, Settings, Activity, User, Pencil, X, Loader2, AlertTriangle, AlertCircle, RefreshCw, Zap, Calendar, Sun as SunIcon, DollarSign as DollarSignIcon, Battery as BatteryIcon, Download, Cpu } from 'lucide-react';
import { getSite, getUser, updateSite, getLiveTelemetry, getCurrentTelemetry, getConnectionStatus, getNEMDetailedPrices, fetchAmberSites, fetchSolcastResources, getSitePrices, getSiteForecast, getSiteProviderStatus, getOptimizationPlan, getEnergyFlow, getCostTotals, triggerOptimization, refreshForecast, refreshPrices, getSiteCredentials, exportSiteDiagnostics, getDistributorTariffs, matchDistributorTariffs, getEVStatus, EVChargerStatus, LiveTelemetryResponse, CurrentTelemetryResponse, SolarControlState, ConnectionStatus, NEMDetailedPrices, AmberSite, SolcastResource, SitePrices, SiteForecast, SiteProviderStatus, CostTotalsResponse, SiteCredentials, DistributorTariff } from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { SiteDetail as SiteDetailType, User as UserType, SiteLiveData, EnergyFlowDataPoint, BatteryPlanDataPoint, SiteUpdate } from '../types';
import {
  HorseshoeGauge,
  solarColorStops,
  loadColorStops,
  SolarForecastCard,
  BatteryStatusCard,
  EnergyFlowChart,
  BatteryPlanChart,
  PriceBlocksCard,
  PowerFlowCard,
  SimulatedPricesCard,
  EVChargersSection,
} from '../components/site';
import { SectionHeader, CredentialField, formatCredValue, SOLAR_INVERTERS, SOLAR_CONTROL_FIELDS } from '../components/site/SiteFormHelpers';
import {
  generateMockLiveData,
  generateMockBatteryPlanData,
} from '../utils/mockSiteData';
import { GLOBIRD_FREE_WINDOW_OPTIONS, resolveFreeWindow } from '../utils/globird';
import {
  defaultFlowPowerForm,
  flowPowerCredentials,
  flowPowerFormFromCredentials,
  resolveHappyHourWindow,
  type FlowPowerForm,
} from '../utils/flowpower';
import { FlowPowerPlanFields } from '../components/site/FlowPowerPlanFields';
import { HappyHourCard } from '../components/site/HappyHourCard';
import PerformanceMetrics from '../components/PerformanceMetrics';
import { Toggle } from '../components/Toggle';

// Available options for dropdowns
// Providers whose prices are per-site (poller writes prices:site:<id>) rather
// than regional wholesale. Fixed retail plans are site-specific like Amber's.
const SITE_PRICED_PROVIDERS = ['amber', 'simulated', 'globird_zerohero', 'flowpower_happy_hour'];
const hasSitePrices = (provider?: string | null): boolean =>
  !!provider && SITE_PRICED_PROVIDERS.includes(provider);

const PRICE_PROVIDERS = ['amber', 'aemo', 'simulated', 'globird_zerohero', 'flowpower_happy_hour'];

// User-facing labels for price providers (avoid surfacing raw keys like
// "globird_zerohero" in the UI).
const priceProviderLabel = (provider?: string | null): string => {
  switch (provider) {
    case 'aemo':
      return 'AEMO (Free)';
    case 'simulated':
      return 'Simulated (Testing)';
    case 'amber':
      return 'Amber Electric';
    case 'globird_zerohero':
      return 'GloBird ZeroHero';
    case 'flowpower_happy_hour':
      return 'Flow Power Happy Hour';
    case null:
    case undefined:
    case '':
      return 'Not set';
    default:
      return provider.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

// User-facing labels for forecast providers (avoid surfacing raw provider keys
// like "open_meteo" in the UI).
const forecastProviderLabel = (provider?: string | null): string => {
  switch (provider) {
    case 'open_meteo':
      return 'SolanIQ Forecaster';
    case 'forecast_solar':
      return 'Forecast.Solar (Free)';
    case 'simulated':
      return 'Simulated (Testing)';
    case 'solcast':
      return 'Solcast';
    case null:
    case undefined:
    case '':
      return 'Not set';
    default:
      return provider.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
};
const OPTIMIZER_ENGINES = [
  { value: 'emhass', label: 'SolanIQ Optimizer' },
  { value: 'simple_rule', label: 'Simple Rule' },
];

// Aggressiveness presets for the SolanIQ Optimizer (EMHASS engine). These
// labels mirror OPTIMIZATION_MODE_PRESETS in shared/adapters/optimizer/emhass.py
// — keep in sync. The values are the keys EMHASS reads from optimizer_config.
const EMHASS_MODE_PRESETS: { value: string; label: string; description: string }[] = [
  {
    value: 'self_consumption',
    label: 'Conservative (longevity)',
    description: 'Prioritises battery life. Light cycling, minimal grid arbitrage.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Default. Moderate cycling, runs solar-first with some arbitrage.',
  },
  {
    value: 'maximum_profit',
    label: 'Aggressive (max profit)',
    description: 'Heavy cycling. Charges from cheap grid windows, discharges into peaks.',
  },
];

// Conservative defaults for the SOC bounds shown in the form. These match
// DEFAULT_OPTIMIZER_CONFIG in shared/adapters/optimizer/emhass.py.
const EMHASS_SOC_DEFAULTS = {
  battery_minimum_state_of_charge: 0.20,
  battery_maximum_state_of_charge: 1.0,
};
const BATTERY_ADAPTERS = [
  { value: 'esy_sunhome', label: 'ESY Sunhome', comingSoon: false },
  { value: 'demo', label: 'Demo/Simulation Mode', comingSoon: false },
  { value: 'alpha_ess', label: 'AlphaESS', comingSoon: true },
  { value: 'byd', label: 'BYD Battery', comingSoon: true },
  { value: 'tesla_gateway', label: 'Tesla Powerwall', comingSoon: true },
];
const REGIONS = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// Simulation scenarios available for testing
const SIMULATION_SCENARIOS: { id: string; name: string; description: string }[] = [
  { id: 'sunny_day_tou', name: 'Sunny Day with TOU', description: 'Perfect solar day with time-of-use pricing. Tests basic arbitrage.' },
  { id: 'cloudy_day_volatile', name: 'Cloudy Day + Volatile Prices', description: 'Overcast with wholesale price volatility. Tests forecast uncertainty.' },
  { id: 'solar_sponge', name: 'Solar Sponge Pricing', description: 'Negative midday prices, high evening. Tests grid charging during surplus.' },
  { id: 'extreme_event', name: 'Extreme Price Event', description: 'Major price volatility with spikes. Stress tests safety limits.' },
  { id: 'ev_overnight', name: 'EV Charging Overnight', description: 'Home with EV charging overnight. Tests off-peak arbitrage.' },
  { id: 'small_system', name: 'Small System', description: '5kW solar with 5kWh battery. Tests constraint handling.' },
  { id: 'commercial', name: 'Commercial Site', description: 'Commercial 9-5 load pattern with large system.' },
];

// Provider credential requirements
// Note: Amber needs 'site_id' which is fetched dynamically after API key is entered
// Solcast 'resource_ids' is a multi-select fetched dynamically after API key is entered
const PRICE_PROVIDER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean; dynamic?: boolean }[]> = {
  amber: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'Enter your Amber API key', required: true },
    { label: 'Site', key: 'site_id', type: 'select', placeholder: 'Select your Amber site', required: true, dynamic: true },
  ],
  aemo: [],
  simulated: [], // No credentials - uses scenario config
  globird_zerohero: [
    // Fixed-tariff plan: user picks a distributor tariff (its id), the plan's
    // ZeroCharge free-charging window, + optional state override. Import rate
    // comes from the selected tariff; the ZeroHero overlay (free window,
    // feed-in, 6-9pm super-export) is applied automatically.
    { label: 'Distributor Tariff ID', key: 'tariff_id', type: 'text', placeholder: 'Pick from /distributor-tariffs', required: true },
    { label: 'Free Charging Window', key: 'free_window_start', type: 'select', placeholder: '' },
    { label: 'State (optional)', key: 'state', type: 'text', placeholder: 'e.g. VIC — defaults to site region' },
  ],
};

const FORECAST_PROVIDER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean; dynamic?: boolean }[]> = {
  solcast: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'Enter your Solcast API key', required: true },
    { label: 'Resources', key: 'resource_ids', type: 'multiselect', placeholder: 'Select your Solcast rooftop resources', required: true, dynamic: true },
  ],
  forecast_solar: [],
  simulated: [], // No credentials - uses scenario config
};

const BATTERY_ADAPTER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean; hint?: string }[]> = {
  esy_sunhome: [
    { label: 'Username', key: 'username', type: 'text', placeholder: 'ESY app username or email', required: true },
    { label: 'Password', key: 'password', type: 'password', placeholder: 'ESY app password', required: true },
    { label: 'Device Serial Number', key: 'device_sn', type: 'text', placeholder: 'Leave blank to auto-discover', hint: 'Optional - will be fetched automatically from your account' },
  ],
  demo: [], // No credentials - uses scenario config
  alpha_ess: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'AlphaESS API key', required: true },
    { label: 'Serial Number', key: 'serial_number', type: 'text', placeholder: 'Device serial number', required: true },
  ],
  byd: [
    { label: 'Host / IP Address', key: 'host', type: 'text', placeholder: 'e.g. 192.168.1.100', required: true },
    { label: 'Username', key: 'username', type: 'text', placeholder: 'BYD username' },
    { label: 'Password', key: 'password', type: 'password', placeholder: 'BYD password' },
  ],
  tesla_gateway: [
    { label: 'Email', key: 'email', type: 'email', placeholder: 'Tesla account email', required: true },
    { label: 'Refresh Token', key: 'refresh_token', type: 'password', placeholder: 'Tesla API refresh token', required: true },
  ],
};

// Map the API's solar_control state onto SiteLiveData.solar.curtailment.
// Only decisions that actually limit PV surface an indicator: an applied
// limit must be confirmed (active), a suggestion-mode limit shows as a plan.
function mapSolarCurtailment(
  solarControl?: SolarControlState
): SiteLiveData['solar']['curtailment'] {
  if (!solarControl) return undefined;
  const limiting =
    solarControl.mode === 'applied'
      ? solarControl.active
      : solarControl.action === 'limit';
  if (!limiting) return undefined;
  return {
    active: true,
    mode: solarControl.mode,
    limit_w: solarControl.limit_w,
  };
}

// Convert current telemetry response (from Redis) to SiteLiveData format
function convertCurrentTelemetryToSiteLiveData(
  response: CurrentTelemetryResponse,
  batteryCapacityWh?: number
): SiteLiveData {
  // Determine battery mode based on power and status
  let batteryMode: SiteLiveData['battery']['mode'] = 'idle';
  const batteryStatusText = response.battery?.status_text?.toLowerCase() || '';
  const batteryPower = response.battery?.power_w || 0;
  const pvPower = response.pv?.power_w || 0;
  const gridPower = response.grid?.power_w || 0;

  if (batteryStatusText === 'discharging' || batteryPower > 0) {
    batteryMode = 'discharging';
  } else if (batteryStatusText === 'charging' || batteryPower < 0) {
    // Check if charging from solar or grid
    if (pvPower > Math.abs(batteryPower)) {
      batteryMode = 'solar_charge';
    } else if (gridPower > 0) {
      batteryMode = 'grid_charge';
    } else {
      batteryMode = 'charging';
    }
  }

  return {
    timestamp: response.timestamp,
    solar: {
      power_w: pvPower,
      daily_kwh: response.pv?.daily_generation_kwh || 0,
      curtailment: mapSolarCurtailment(response.solar_control),
    },
    load: {
      power_w: response.load?.power_w || 0,
      daily_kwh: response.load?.daily_consumption_kwh || 0,
    },
    battery: {
      soc_percent: response.battery?.soc_percent || 0,
      power_w: batteryPower,
      capacity_kwh: batteryCapacityWh ? batteryCapacityWh / 1000 : 10,
      mode: batteryMode,
      export_limit_w:
        response.export_limit?.restricted && response.export_limit.watts != null
          ? response.export_limit.watts
          : null,
    },
    grid: {
      power_w: gridPower,
      import_price: 0.25, // TODO: Fetch from price provider
      export_price: 0.08,
    },
    forecast: {
      this_hour_wh: 0,
      next_hour_wh: 0,
      remaining_today_kwh: 0,
      today_total_kwh: response.pv?.daily_generation_kwh || 0,
      tomorrow_kwh: 0,
    },
    optimizer: {
      mode: batteryMode,
      description: response.system?.run_mode_text || 'Monitoring',
      battery_signal_w: batteryPower,
      grid_signal_w: gridPower,
      interval_balance_wh: 0,
      interval_progress_percent: 0,
      billing_interval_minutes: 5,
    },
  };
}

// Convert live telemetry response to SiteLiveData format (fallback)
function convertLiveTelemetryToSiteLiveData(
  response: LiveTelemetryResponse,
  batteryCapacityWh?: number
): SiteLiveData {
  const { telemetry } = response;

  // Determine battery mode based on power and status
  let batteryMode: SiteLiveData['battery']['mode'] = 'idle';
  if (telemetry.battery.power_w > 100) {
    batteryMode = 'discharging';
  } else if (telemetry.battery.power_w < -100) {
    // Check if charging from solar or grid
    if (telemetry.pv.power_w > Math.abs(telemetry.battery.power_w)) {
      batteryMode = 'solar_charge';
    } else if (telemetry.grid.power_w > 0) {
      batteryMode = 'grid_charge';
    } else {
      batteryMode = 'charging';
    }
  }

  return {
    timestamp: telemetry.timestamp,
    solar: {
      power_w: telemetry.pv.power_w,
      daily_kwh: telemetry.pv.daily_generation_kwh,
      curtailment: mapSolarCurtailment(response.solar_control),
    },
    load: {
      power_w: telemetry.load.power_w,
      daily_kwh: telemetry.load.daily_consumption_kwh,
    },
    battery: {
      soc_percent: telemetry.battery.soc_percent,
      power_w: telemetry.battery.power_w,
      capacity_kwh: batteryCapacityWh ? batteryCapacityWh / 1000 : 10, // Default 10kWh if not configured
      mode: batteryMode,
    },
    grid: {
      power_w: telemetry.grid.power_w,
      import_price: 0.25, // TODO: Fetch from price provider
      export_price: 0.08,
    },
    // Forecast data not available from live telemetry - use placeholder
    forecast: {
      this_hour_wh: 0,
      next_hour_wh: 0,
      remaining_today_kwh: 0,
      today_total_kwh: telemetry.pv.daily_generation_kwh,
      tomorrow_kwh: 0,
    },
    optimizer: {
      mode: batteryMode,
      description: telemetry.system.run_mode_text || 'Monitoring',
      battery_signal_w: telemetry.battery.power_w,
      grid_signal_w: telemetry.grid.power_w,
      interval_balance_wh: 0,
      interval_progress_percent: 0,
      billing_interval_minutes: 5,
    },
  };
}

export function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();

  // Poll the EV charger status alongside telemetry so the power flow card
  // can draw the EV bubble. Cheap (one Redis read) and 404 -> null.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await getEVStatus(siteId);
        if (!cancelled) setEvStatus(status);
      } catch (err) {
        console.error('Failed to fetch EV status:', err);
      }
    };
    tick();
    const interval = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [siteId]);
  const { isAdmin, user: authUser } = useAuth();
  const [site, setSite] = useState<SiteDetailType | null>(null);
  const [owner, setOwner] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin action states
  const [triggeringOptimization, setTriggeringOptimization] = useState<string | null>(null);
  const [refreshingForecast, setRefreshingForecast] = useState(false);
  const [exportingDiagnostics, setExportingDiagnostics] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  // Live telemetry data
  const [liveData, setLiveData] = useState<SiteLiveData | null>(null);
  // Live EV charger status for the power flow card (null = no charger).
  const [evStatus, setEvStatus] = useState<EVChargerStatus | null>(null);
  const [isLiveData, setIsLiveData] = useState(false);
  const [telemetryError, setTelemetryError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [energyFlowData, setEnergyFlowData] = useState<EnergyFlowDataPoint[]>([]);
  const [batteryPlanData, setBatteryPlanData] = useState<BatteryPlanDataPoint[]>([]);

  // NEM wholesale prices (for AEMO provider)
  const [nemPrices, setNemPrices] = useState<NEMDetailedPrices | null>(null);
  const [nemPricesLoading, setNemPricesLoading] = useState(false);
  const [nemPricesError, setNemPricesError] = useState<string | null>(null);

  // Site-specific prices (for Amber provider)
  const [sitePrices, setSitePrices] = useState<SitePrices | null>(null);
  const [sitePricesLoading, setSitePricesLoading] = useState(false);

  // Cost totals
  const [costTotals, setCostTotals] = useState<{ today: CostTotalsResponse | null; week: CostTotalsResponse | null; month: CostTotalsResponse | null }>({ today: null, week: null, month: null });

  // Solar forecast
  const [solarForecast, setSolarForecast] = useState<SiteForecast | null>(null);

  // Provider status
  const [providerStatus, setProviderStatus] = useState<SiteProviderStatus | null>(null);

  // Edit modal state
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [siteFormData, setSiteFormData] = useState<Partial<SiteUpdate> & { price_provider_credentials?: Record<string, unknown>; forecast_provider_credentials?: Record<string, unknown>; battery_credentials?: Record<string, unknown> }>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // GloBird tariffs for the edit-dialog selector. Matched to the site's postcode
  // (CDR geography) when available, else filtered by state.
  const [globirdTariffs, setGlobirdTariffs] = useState<DistributorTariff[]>([]);

  // Edit Site dialog accordion state — only one section open at a time so the
  // dialog stays short. Battery is the most-edited so it's open by default.
  type SiteSectionKey = 'battery' | 'solar' | 'price' | 'optimizer' | null;
  const [openSiteSection, setOpenSiteSection] = useState<SiteSectionKey>('battery');

  // Lazily-loaded current credentials shown via per-field "Show current"
  // toggles. Fetched once per dialog open from /sites/{id}/credentials.
  const [currentSiteCreds, setCurrentSiteCreds] = useState<SiteCredentials | null>(null);
  // Flow Power plan fields in the edit dialog. Kept as its own form state (c/kWh
  // strings) so typing a decimal isn't rounded away by the $/kWh round trip;
  // every change also rewrites price_provider_credentials in full.
  const [flowpowerForm, setFlowpowerForm] = useState<FlowPowerForm>(defaultFlowPowerForm);
  const [siteCredsLoading, setSiteCredsLoading] = useState(false);
  const [revealedSiteFields, setRevealedSiteFields] = useState<Set<string>>(new Set());

  // Dynamic provider options state
  const [amberSites, setAmberSites] = useState<AmberSite[]>([]);
  const [solcastResources, setSolcastResources] = useState<SolcastResource[]>([]);
  const [loadingAmberSites, setLoadingAmberSites] = useState(false);
  const [loadingSolcastResources, setLoadingSolcastResources] = useState(false);
  const [amberSitesError, setAmberSitesError] = useState<string | null>(null);
  const [solcastResourcesError, setSolcastResourcesError] = useState<string | null>(null);

  // Simulation mode toggle state
  const [togglingMode, setTogglingMode] = useState(false);

  // Load GloBird tariffs for the edit-dialog selector when that provider is
  // chosen. Prefer a postcode match (CDR geography); fall back to state filter.
  useEffect(() => {
    if (!showSiteModal || siteFormData.price_provider !== 'globird_zerohero') return;
    let cancelled = false;
    const pc = (site?.postcode || '').trim();
    const st = (site?.region || '').toUpperCase().replace(/[0-9]+$/, '');
    const load = /^\d{4}$/.test(pc)
      ? matchDistributorTariffs(pc)
      : getDistributorTariffs({ retailer: 'globird', state: st || undefined });
    load
      .then((rows) => {
        if (!cancelled) setGlobirdTariffs(rows);
      })
      .catch(() => {
        if (!cancelled) setGlobirdTariffs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showSiteModal, siteFormData.price_provider, site?.postcode, site?.region]);

  // Fetch Amber sites when API key changes
  const handleFetchAmberSites = useCallback(async () => {
    const apiKey = siteFormData.price_provider_credentials?.api_key as string;
    if (!apiKey || apiKey.length < 10) {
      setAmberSitesError('Please enter a valid API key first');
      return;
    }

    setLoadingAmberSites(true);
    setAmberSitesError(null);
    try {
      const sites = await fetchAmberSites(apiKey);
      setAmberSites(sites);
      // If only one site, auto-select it
      if (sites.length === 1) {
        setSiteFormData(prev => ({
          ...prev,
          price_provider_credentials: {
            ...prev.price_provider_credentials,
            site_id: sites[0].id,
          },
        }));
      }
    } catch (err) {
      setAmberSitesError(err instanceof Error ? err.message : 'Failed to fetch Amber sites');
      setAmberSites([]);
    } finally {
      setLoadingAmberSites(false);
    }
  }, [siteFormData.price_provider_credentials?.api_key]);

  // Fetch Solcast resources when API key changes
  const handleFetchSolcastResources = useCallback(async () => {
    const apiKey = siteFormData.forecast_provider_credentials?.api_key as string;
    if (!apiKey || apiKey.length < 10) {
      setSolcastResourcesError('Please enter a valid API key first');
      return;
    }

    setLoadingSolcastResources(true);
    setSolcastResourcesError(null);
    try {
      const resources = await fetchSolcastResources(apiKey);
      setSolcastResources(resources);
      // If only one resource, auto-select it
      if (resources.length === 1) {
        setSiteFormData(prev => ({
          ...prev,
          forecast_provider_credentials: {
            ...prev.forecast_provider_credentials,
            resource_ids: [resources[0].resource_id],
          },
        }));
      }
    } catch (err) {
      setSolcastResourcesError(err instanceof Error ? err.message : 'Failed to fetch Solcast resources');
      setSolcastResources([]);
    } finally {
      setLoadingSolcastResources(false);
    }
  }, [siteFormData.forecast_provider_credentials?.api_key]);

  // Cache for the "generated today" value shown on the SolarForecastCard.
  // The raw inverter value updates every 10s telemetry tick, which makes the
  // card visibly tick — but the figure only meaningfully changes over minutes.
  // We snapshot it once an hour (and immediately when the day rolls over) so
  // the displayed value is stable between refreshes.
  const todayKwhCacheRef = useRef<{ value: number; at: number } | null>(null);
  const TODAY_KWH_REFRESH_MS = 60 * 60 * 1000; // 1 hour

  const getThrottledTodayKwh = useCallback((rawKwh: number): number => {
    const now = Date.now();
    const last = todayKwhCacheRef.current;
    const dayBoundary = !last || rawKwh < last.value; // counter resets at midnight
    if (!last || dayBoundary || now - last.at >= TODAY_KWH_REFRESH_MS) {
      todayKwhCacheRef.current = { value: rawKwh, at: now };
      return rawKwh;
    }
    return last.value;
  }, []);

  // Function to fetch telemetry - tries /current (Redis) first, then /live
  const fetchLiveTelemetry = async (siteData: SiteDetailType) => {
    // Try to get current telemetry from Redis first (populated by battery_interface service)
    try {
      const currentTelemetry = await getCurrentTelemetry(siteData.id);
      const convertedData = convertCurrentTelemetryToSiteLiveData(
        currentTelemetry,
        siteData.battery_config?.capacity_wh as number
      );
      convertedData.forecast.today_total_kwh = getThrottledTodayKwh(
        currentTelemetry.pv?.daily_generation_kwh || 0
      );
      setLiveData(convertedData);
      setIsLiveData(true);
      setTelemetryError(null);
      return true;
    } catch {
      // Current telemetry not available, try live endpoint as fallback
      try {
        const liveTelemetry = await getLiveTelemetry(siteData.id);
        const convertedData = convertLiveTelemetryToSiteLiveData(
          liveTelemetry,
          siteData.battery_config?.capacity_wh as number
        );
        convertedData.forecast.today_total_kwh = getThrottledTodayKwh(
          liveTelemetry.telemetry.pv.daily_generation_kwh || 0
        );
        setLiveData(convertedData);
        setIsLiveData(true);
        setTelemetryError(null);
        return true;
      } catch (err) {
        console.error('Failed to fetch telemetry:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setTelemetryError(errorMessage);
        return false;
      }
    }
  };

  // Function to fetch NEM prices (for AEMO provider or when no provider configured)
  const fetchNEMPrices = async (region: string) => {
    // Map region codes to NEM region format
    const nemRegion = region.replace(/1$/, ''); // Remove trailing "1" if present
    const validRegions = ['NSW', 'VIC', 'QLD', 'SA', 'TAS'];

    if (!validRegions.includes(nemRegion)) {
      setNemPricesError(`NEM prices not available for region: ${region}`);
      return;
    }

    try {
      setNemPricesLoading(true);
      setNemPricesError(null);
      const prices = await getNEMDetailedPrices(nemRegion);
      setNemPrices(prices);
    } catch (err) {
      console.error('Failed to fetch NEM prices:', err);
      setNemPricesError(err instanceof Error ? err.message : 'Failed to fetch prices');
    } finally {
      setNemPricesLoading(false);
    }
  };

  // Function to fetch site-specific prices (for Amber provider)
  const fetchSitePrices = async (siteId: string) => {
    try {
      setSitePricesLoading(true);
      const prices = await getSitePrices(siteId);
      setSitePrices(prices);
    } catch (err) {
      console.error('Failed to fetch site prices:', err);
      // Not critical - silently fail
    } finally {
      setSitePricesLoading(false);
    }
  };

  // Function to fetch solar forecast
  const fetchSolarForecast = async (siteId: string) => {
    try {
      const forecast = await getSiteForecast(siteId);
      setSolarForecast(forecast);
    } catch (err) {
      console.error('Failed to fetch solar forecast:', err);
      // Not critical - silently fail
    }
  };

  // Function to fetch provider status
  const fetchProviderStatus = async (siteId: string) => {
    try {
      const status = await getSiteProviderStatus(siteId);
      setProviderStatus(status);
    } catch (err) {
      console.error('Failed to fetch provider status:', err);
      // Not critical - silently fail
    }
  };

  // Function to fetch cost totals
  const fetchCostTotals = async (siteId: string) => {
    try {
      const [today, week, month] = await Promise.all([
        getCostTotals(siteId, 'today'),
        getCostTotals(siteId, 'week'),
        getCostTotals(siteId, 'month'),
      ]);
      setCostTotals({ today, week, month });
    } catch (err) {
      console.error('Failed to fetch cost totals:', err);
      // Not critical - silently fail
    }
  };

  // Function to fetch optimization plan and convert to chart data
  const fetchOptimizationPlanData = async (siteId: string) => {
    try {
      const plan = await getOptimizationPlan(siteId);

      // Convert to BatteryPlanDataPoint format for the chart
      if (plan.schedule && plan.schedule.length > 0) {
        // Hide the last 4 hours of the plan as end-of-horizon results are unreliable
        // (optimizer can't see beyond the horizon, so decisions become less optimal)
        // 4 hours = 8 intervals at 30-minute spacing
        const INTERVALS_TO_HIDE = 8;
        const visibleSchedule = plan.schedule.slice(0, -INTERVALS_TO_HIDE);

        // Only show if we still have data after filtering
        if (visibleSchedule.length > 0) {
          const chartData: BatteryPlanDataPoint[] = visibleSchedule.map((point) => ({
            timestamp: point.timestamp,
            battery_power_w: point.power_w,
            grid_power_w: point.grid_power_w ?? point.power_w, // Fall back to battery power if grid not available
            pv_power_w: point.pv_power_w ?? 0, // Solar/PV forecast
            soc_percent: (point.soc ?? 0.5) * 100, // Convert to percentage
            import_price: point.import_price ?? 0.30, // Use API price or default
            export_price: point.export_price ?? 0.05,
            ev_power_w: point.ev_power_w ?? null, // Planned EV charging (only when an EV is in the plan)
          }));
          setBatteryPlanData(chartData);
        } else {
          // Fall back to mock data if filtering removed all data
          setBatteryPlanData(generateMockBatteryPlanData());
        }
      } else {
        // Fall back to mock data if no plan available
        setBatteryPlanData(generateMockBatteryPlanData());
      }
    } catch (err) {
      console.error('Failed to fetch optimization plan:', err);
      // Fall back to mock data
      setBatteryPlanData(generateMockBatteryPlanData());
    }
  };

  // Function to fetch energy flow data for the chart
  const fetchEnergyFlowData = async (siteId: string) => {
    try {
      const energyData = await getEnergyFlow(siteId, 24);

      // Convert to EnergyFlowDataPoint format for the chart. An empty result
      // renders an empty chart — never fabricate bars with mock data on a
      // real site's dashboard.
      const chartData: EnergyFlowDataPoint[] = (energyData.intervals || []).map((point) => ({
        timestamp: point.timestamp,
        import_wh: point.import_wh,
        consumption_wh: point.consumption_wh,
        production_wh: point.production_wh,
        export_wh: point.export_wh,
      }));
      setEnergyFlowData(chartData);
    } catch (err) {
      // Keep whatever was last shown; a transient fetch failure shouldn't
      // blank (or worse, fake) the chart.
      console.error('Failed to fetch energy flow data:', err);
    }
  };

  // Admin function to trigger optimization
  const handleTriggerOptimization = async (type: 'dayahead' | 'mpc') => {
    if (!siteId || triggeringOptimization) return;
    setTriggeringOptimization(type);
    try {
      await triggerOptimization(siteId, type);
      // Refresh the optimization plan after a short delay
      setTimeout(() => {
        fetchOptimizationPlanData(siteId);
      }, 2000);
    } catch (err) {
      console.error(`Failed to trigger ${type} optimization:`, err);
    } finally {
      setTriggeringOptimization(null);
    }
  };

  // Admin function: download a JSON diagnostics bundle for offline analysis
  const handleExportDiagnostics = async () => {
    if (!siteId || exportingDiagnostics) return;
    setExportingDiagnostics(true);
    try {
      const data = await exportSiteDiagnostics(siteId);
      const safeName = (site?.name || 'site').replace(/[^a-z0-9-_]+/gi, '_');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solaniq-diagnostics-${safeName}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export diagnostics:', err);
      alert('Failed to export diagnostics. Check the console for details.');
    } finally {
      setExportingDiagnostics(false);
    }
  };

  // Admin function to refresh forecast
  const handleRefreshForecast = async () => {
    if (!siteId || refreshingForecast) return;
    setRefreshingForecast(true);
    try {
      await refreshForecast(siteId);
      // Refresh the forecast data
      const newForecast = await getSiteForecast(siteId);
      setSolarForecast(newForecast);
    } catch (err) {
      console.error('Failed to refresh forecast:', err);
    } finally {
      setRefreshingForecast(false);
    }
  };

  // Admin function to refresh simulated prices
  const handleRefreshPrices = async () => {
    if (!siteId || refreshingPrices) return;
    setRefreshingPrices(true);
    try {
      await refreshPrices(siteId);
      // Refresh the price data
      const newPrices = await getSitePrices(siteId);
      setSitePrices(newPrices);
    } catch (err) {
      console.error('Failed to refresh prices:', err);
    } finally {
      setRefreshingPrices(false);
    }
  };

  useEffect(() => {
    async function fetchSite() {
      if (!siteId) return;
      try {
        setLoading(true);
        const siteData = await getSite(siteId);
        setSite(siteData);

        // Fetch owner details
        try {
          const userData = await getUser(siteData.user_id);
          setOwner(userData);
        } catch {
          // Owner fetch failed, continue without it
        }

        // Fetch connection status
        try {
          const status = await getConnectionStatus(siteData.id);
          setConnectionStatus(status);
        } catch {
          // Connection status fetch failed, continue without it
        }

        // Try to fetch live telemetry first
        const liveSuccess = await fetchLiveTelemetry(siteData);

        // Fall back to mock data if live telemetry fails
        if (!liveSuccess) {
          setLiveData(generateMockLiveData());
          setIsLiveData(false);
        }

        // Generate chart data
        // Fetch real energy flow data (falls back to mock if no data)
        fetchEnergyFlowData(siteData.id);

        // Fetch real optimization plan data
        fetchOptimizationPlanData(siteData.id);

        // Fetch prices based on provider
        if (hasSitePrices(siteData.price_provider)) {
          // Site-priced provider (Amber / Simulated / GloBird): per-site tariffs
          fetchSitePrices(siteData.id);
        } else {
          // AEMO or no provider: fetch regional NEM prices
          fetchNEMPrices(siteData.region);
        }

        // Always try to fetch solar forecast
        fetchSolarForecast(siteData.id);

        // Fetch provider status
        fetchProviderStatus(siteData.id);

        // Fetch cost totals
        fetchCostTotals(siteData.id);
      } catch (err) {
        setError('Failed to load site data');
      } finally {
        setLoading(false);
      }
    }
    fetchSite();
  }, [siteId]);

  // Refresh live data and connection status periodically
  useEffect(() => {
    if (!site) return;

    const refreshData = async () => {
      // Refresh connection status
      try {
        const status = await getConnectionStatus(site.id);
        setConnectionStatus(status);
      } catch {
        // Ignore errors
      }

      // Refresh telemetry
      const liveSuccess = await fetchLiveTelemetry(site);
      if (!liveSuccess && !isLiveData) {
        // Only update mock data if we weren't already showing live data
        setLiveData(generateMockLiveData());
      }
    };

    const interval = setInterval(refreshData, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [site, isLiveData]);

  // Refresh prices and optimization plan (5-minute interval)
  useEffect(() => {
    if (!site) return;

    const refreshPricesAndPlan = () => {
      // Refresh prices
      if (hasSitePrices(site.price_provider)) {
        fetchSitePrices(site.id);
      } else {
        fetchNEMPrices(site.region);
      }
      // Refresh optimization plan
      fetchOptimizationPlanData(site.id);
    };

    const interval = setInterval(refreshPricesAndPlan, 300000); // Update every 5 minutes

    return () => clearInterval(interval);
  }, [site]);

  // Refresh solar forecast (hourly - matches poller)
  useEffect(() => {
    if (!site) return;

    const refreshForecast = () => {
      fetchSolarForecast(site.id);
    };

    const interval = setInterval(refreshForecast, 3600000); // Update every hour

    return () => clearInterval(interval);
  }, [site]);

  // Refresh provider status (every 30 seconds)
  useEffect(() => {
    if (!site) return;

    const refreshStatus = () => {
      fetchProviderStatus(site.id);
    };

    const interval = setInterval(refreshStatus, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [site]);

  // Refresh energy flow data (every 30 minutes, at :00 and :30)
  useEffect(() => {
    if (!site) return;

    const refreshEnergyFlow = () => {
      fetchEnergyFlowData(site.id);
    };

    // Calculate time until next half-hour boundary
    const now = new Date();
    const minutesUntilNextHalfHour = now.getMinutes() < 30
      ? 30 - now.getMinutes()
      : 60 - now.getMinutes();
    const msUntilNextHalfHour = (minutesUntilNextHalfHour * 60 - now.getSeconds()) * 1000;

    // First refresh at next half-hour, then every 30 minutes. The interval
    // id must live in the effect scope: a return value from the setTimeout
    // callback is discarded, so the old inline cleanup never ran and the
    // interval leaked past unmount.
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      refreshEnergyFlow();
      // Then refresh every 30 minutes
      interval = setInterval(refreshEnergyFlow, 1800000); // 30 minutes
    }, msUntilNextHalfHour + 5000); // Add 5 seconds buffer for data to be ready

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [site]);

  // Derive the forecast that the SolarForecastCard actually displays at
  // render time, by merging Solcast-derived hourly fields onto the
  // telemetry-driven liveData.forecast. Doing this in useMemo (not as a
  // post-render state update) means React commits the final merged values
  // in a single pass — there's no intermediate "zero" state for the user
  // to see flicker through. ``today_total_kwh`` is intentionally taken
  // from liveData.forecast (sourced from throttled inverter telemetry),
  // not from the Solcast forecasted total.
  const displayedForecast = useMemo(() => {
    if (!liveData) return null;
    if (!solarForecast) return liveData.forecast;

    const now = new Date();
    const currentHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours());
    const nextHour = new Date(currentHour.getTime() + 60 * 60 * 1000);
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    let thisHourWh = 0;
    let nextHourWh = 0;
    let remainingTodayKwh = 0;

    for (const point of solarForecast.forecast) {
      const pointTime = new Date(point.timestamp);
      // 30-minute intervals, so value_w * 0.5 = Wh for that interval
      const intervalWh = point.value_w * 0.5;

      if (pointTime >= currentHour && pointTime < nextHour) {
        thisHourWh += intervalWh;
      }
      if (pointTime >= nextHour && pointTime < new Date(nextHour.getTime() + 60 * 60 * 1000)) {
        nextHourWh += intervalWh;
      }
      if (pointTime > now && pointTime <= todayEnd) {
        remainingTodayKwh += intervalWh / 1000;
      }
    }

    return {
      ...liveData.forecast,
      this_hour_wh: Math.round(thisHourWh),
      next_hour_wh: Math.round(nextHourWh),
      remaining_today_kwh: Math.round(remainingTodayKwh * 10) / 10,
      tomorrow_kwh: solarForecast.summary.tomorrow_kwh,
    };
  }, [solarForecast, liveData]);

  // Update liveData grid prices when sitePrices is available (Amber)
  useEffect(() => {
    if (!sitePrices || !liveData) return;

    setLiveData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        grid: {
          ...prev.grid,
          import_price: sitePrices.current.import,
          export_price: sitePrices.current.export,
        },
      };
    });
  }, [sitePrices, liveData?.timestamp]);

  // Update liveData grid prices when nemPrices is available (AEMO)
  useEffect(() => {
    if (!nemPrices || !liveData || hasSitePrices(site?.price_provider)) return;

    // Use current 30-min estimate as the import price
    // AEMO wholesale doesn't have separate export price, use same value
    const priceKwh = nemPrices.current_30min?.estimate_kwh || nemPrices.current?.price_kwh || 0;

    setLiveData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        grid: {
          ...prev.grid,
          import_price: priceKwh,
          export_price: priceKwh * 0.8, // Estimate export at 80% of wholesale
        },
      };
    });
  }, [nemPrices, liveData?.timestamp, site?.price_provider]);

  // Helper to check if credentials object has any non-empty values
  const hasCredentials = (creds: Record<string, unknown> | undefined): boolean => {
    if (!creds) return false;
    return Object.values(creds).some((v) => v !== '' && v !== null && v !== undefined);
  };

  const openSiteEditModal = () => {
    if (!site) return;
    setSiteFormData({
      name: site.name,
      region: site.region,
      address_line1: site.address_line1 || '',
      address_line2: site.address_line2 || '',
      city: site.city || '',
      state: site.state || '',
      postcode: site.postcode || '',
      country: site.country || 'Australia',
      price_provider: site.price_provider,
      price_provider_credentials: {},
      forecast_provider: site.forecast_provider,
      forecast_provider_credentials: {},
      optimizer_engine: site.optimizer_engine,
      optimizer_config: site.optimizer_config || {},
      battery_adapter: site.battery_adapter,
      battery_credentials: {},
      battery_config: site.battery_config || {},
      solar_control_adapter: site.solar_control_adapter || '',
      solar_credentials: {},
      solar_config: site.solar_config || {},
      simulation_config: site.simulation_config || { scenario: 'sunny_day_tou' },
      active: site.active,
    });
    setSaveError(null);
    setOpenSiteSection('battery');
    setCurrentSiteCreds(null);
    setRevealedSiteFields(new Set());
    setShowSiteModal(true);
  };

  // Lazy-load the site's credentials the first time any "Show current" eye
  // is clicked. Subsequent clicks just toggle visibility on already-loaded data.
  const ensureSiteCredentialsLoaded = async () => {
    if (!site || currentSiteCreds || siteCredsLoading) return;
    setSiteCredsLoading(true);
    try {
      const data = await getSiteCredentials(site.id);
      setCurrentSiteCreds(data);
    } catch (err) {
      console.error('Failed to load site credentials:', err);
    } finally {
      setSiteCredsLoading(false);
    }
  };

  const toggleRevealedSite = async (fieldId: string) => {
    if (revealedSiteFields.has(fieldId)) {
      const next = new Set(revealedSiteFields);
      next.delete(fieldId);
      setRevealedSiteFields(next);
      return;
    }
    await ensureSiteCredentialsLoaded();
    setRevealedSiteFields(new Set(revealedSiteFields).add(fieldId));
  };

  type SiteCredsBucket = 'battery' | 'price' | 'forecast' | 'solar';
  const getCurrentSiteCredValue = (bucket: SiteCredsBucket, key: string): unknown => {
    if (!currentSiteCreds) return undefined;
    const map = {
      battery: currentSiteCreds.battery_credentials,
      price: currentSiteCreds.price_provider_credentials,
      forecast: currentSiteCreds.forecast_provider_credentials,
      solar: currentSiteCreds.solar_credentials,
    } as const;
    return map[bucket]?.[key];
  };

  // GloBird ZeroHero: the plan's ZeroCharge free-charging window lives in
  // price_provider_credentials (free_window_start/end). Load credentials
  // eagerly for GloBird sites so the plan chart shades the site's actual
  // window, and so the edit dialog's selectors show current values.
  useEffect(() => {
    const wantsGlobirdCreds =
      site?.price_provider === 'globird_zerohero' ||
      site?.price_provider === 'flowpower_happy_hour' ||
      (showSiteModal && siteFormData.price_provider === 'globird_zerohero');
    if (wantsGlobirdCreds && !currentSiteCreds && !siteCredsLoading) {
      void ensureSiteCredentialsLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.id, site?.price_provider, showSiteModal, siteFormData.price_provider, currentSiteCreds, siteCredsLoading]);

  // Seed the edit form's price credentials from the stored ones for GloBird —
  // the form starts empty (partial-edit semantics), but a GloBird save always
  // replaces the whole credentials dict, so changing just the free window
  // must not wipe the selected tariff.
  useEffect(() => {
    if (!showSiteModal || siteFormData.price_provider !== 'globird_zerohero') return;
    const current = currentSiteCreds?.price_provider_credentials;
    if (current && !hasCredentials(siteFormData.price_provider_credentials)) {
      setSiteFormData((prev) => ({
        ...prev,
        price_provider_credentials: { ...current },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSiteModal, currentSiteCreds, siteFormData.price_provider]);

  // Flow Power: seed the plan fields from the stored credentials when the site
  // is already on the plan, else start from the plan defaults. Either way the
  // credentials dict is filled in full, since a save replaces it.
  useEffect(() => {
    if (!showSiteModal || siteFormData.price_provider !== 'flowpower_happy_hour') return;
    if (hasCredentials(siteFormData.price_provider_credentials)) return;
    const onPlan = site?.price_provider === 'flowpower_happy_hour';
    if (onPlan && !currentSiteCreds) return; // wait for the stored values
    const form = onPlan
      ? flowPowerFormFromCredentials(currentSiteCreds?.price_provider_credentials)
      : defaultFlowPowerForm;
    setFlowpowerForm(form);
    setSiteFormData((prev) => ({
      ...prev,
      price_provider_credentials: flowPowerCredentials(form, prev.region ?? site?.region),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSiteModal, currentSiteCreds, siteFormData.price_provider]);

  // Solar inverter control: like GloBird above, a save always replaces the
  // whole solar_credentials dict, so editing one field (e.g. the password)
  // must not wipe the others. Load the stored credentials once the dialog
  // shows an inverter with credential fields, and seed the form from them.
  useEffect(() => {
    const wantsSolarCreds =
      showSiteModal &&
      !!siteFormData.solar_control_adapter &&
      (SOLAR_CONTROL_FIELDS[siteFormData.solar_control_adapter]?.length ?? 0) > 0;
    if (wantsSolarCreds && !currentSiteCreds && !siteCredsLoading) {
      void ensureSiteCredentialsLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSiteModal, siteFormData.solar_control_adapter, currentSiteCreds, siteCredsLoading]);

  useEffect(() => {
    if (!showSiteModal || !siteFormData.solar_control_adapter) return;
    // Only seed when the stored adapter matches the selected one — switching
    // inverters must start from a clean credential form.
    if (siteFormData.solar_control_adapter !== site?.solar_control_adapter) return;
    const current = currentSiteCreds?.solar_credentials;
    if (current && !hasCredentials(siteFormData.solar_credentials)) {
      setSiteFormData((prev) => ({
        ...prev,
        solar_credentials: { ...current },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSiteModal, currentSiteCreds, siteFormData.solar_control_adapter]);

  const handleSaveSite = async () => {
    if (!site) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updateData: SiteUpdate = {
        name: siteFormData.name,
        region: siteFormData.region,
        address_line1: siteFormData.address_line1,
        address_line2: siteFormData.address_line2,
        city: siteFormData.city,
        state: siteFormData.state,
        postcode: siteFormData.postcode,
        country: siteFormData.country,
        price_provider: siteFormData.price_provider,
        forecast_provider: siteFormData.forecast_provider,
        optimizer_engine: siteFormData.optimizer_engine,
        optimizer_config: siteFormData.optimizer_config,
        battery_adapter: siteFormData.battery_adapter,
        battery_config: siteFormData.battery_config,
        solar_control_adapter: siteFormData.solar_control_adapter || null,
        solar_config: siteFormData.solar_config,
        active: siteFormData.active,
      };
      // Include simulation_config if any provider is simulated
      if (
        siteFormData.price_provider === 'simulated' ||
        siteFormData.forecast_provider === 'simulated' ||
        siteFormData.battery_adapter === 'simulated'
      ) {
        updateData.simulation_config = siteFormData.simulation_config || { scenario: 'sunny_day_tou' };
      }
      if (hasCredentials(siteFormData.price_provider_credentials)) {
        updateData.price_provider_credentials = siteFormData.price_provider_credentials;
      }
      if (hasCredentials(siteFormData.forecast_provider_credentials)) {
        updateData.forecast_provider_credentials = siteFormData.forecast_provider_credentials;
      }
      if (hasCredentials(siteFormData.battery_credentials)) {
        updateData.battery_credentials = siteFormData.battery_credentials;
      }
      if (siteFormData.solar_control_adapter && hasCredentials(siteFormData.solar_credentials)) {
        updateData.solar_credentials = siteFormData.solar_credentials;
      }
      const updated = await updateSite(site.id, updateData);
      setSite(updated);
      // Stored credentials may have changed (e.g. GloBird free window) —
      // clear the cache so dependents (plan chart shading) refetch.
      setCurrentSiteCreds(null);
      setShowSiteModal(false);
    } catch (err) {
      setSaveError('Failed to save site');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Handle simulation mode toggle
  const handleToggleSimulationMode = async () => {
    if (!site || togglingMode) return;
    setTogglingMode(true);
    try {
      const updated = await updateSite(site.id, {
        suggestion_mode: !site.suggestion_mode,
      });
      setSite(updated);
    } catch (err) {
      console.error('Failed to toggle simulation mode:', err);
    } finally {
      setTogglingMode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">{error || 'Site not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Site Header */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${site.active ? 'bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-700'}`}>
              <Battery className={`w-8 h-8 ${site.active ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
            </div>
            <div>
              {/* Site name - editable on hover */}
              <button
                onClick={openSiteEditModal}
                className="group flex items-center gap-2 text-left"
              >
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-brand-orange transition-colors">
                  {site.name}
                </h1>
                <Pencil className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              {/* Subtitle: Region / Network / Owner */}
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                <span>{site.region}</span>
                {site.provider_metadata?.network && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">/</span>
                    <span>{site.provider_metadata.network}</span>
                  </>
                )}
                {owner && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">/</span>
                    {authUser?.id === owner.id ? (
                      // Logged-in user is the site owner — link to their Account page.
                      <Link
                        to="/account"
                        className="group inline-flex items-center gap-1 hover:text-brand-orange transition-colors"
                        title="Account settings"
                      >
                        <User className="w-3.5 h-3.5" />
                        <span>{owner.email}</span>
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    ) : (
                      // Admin viewing someone else's site — read-only display; admins
                      // manage other users via /admin/users.
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        <span>{owner.email}</span>
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Suggest / Auto-Pilot toggle (same control as the mobile header) */}
          <div className="flex items-center gap-2">
            <Toggle
              checked={!site.suggestion_mode}
              onChange={() => handleToggleSimulationMode()}
              disabled={togglingMode}
              aria-label="Auto-Pilot"
              title={site.suggestion_mode ? 'Suggest mode: Recommendations are shown but not executed' : 'Auto-Pilot: Actions are executed automatically'}
            />
            <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-[4.5rem]">
              {site.suggestion_mode ? 'Suggest' : 'Auto-Pilot'}
            </span>
            {togglingMode && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Connection Error Banner */}
      {connectionStatus?.status === 'error' && connectionStatus.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Battery Connection Failed
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {connectionStatus.error}
              </p>
              {connectionStatus.retry_count >= connectionStatus.max_retries && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  Maximum retries ({connectionStatus.max_retries}) reached. Update credentials to retry.
                </p>
              )}
              <button
                onClick={openSiteEditModal}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 rounded-lg transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Update Credentials
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Gauges Row */}
      {liveData && (
        <>
        {/* Data Source Indicator and Current Prices */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Live Telemetry</h2>
          <div className="flex items-center gap-4">
            {/* Current prices compact display */}
            {(sitePrices || nemPrices) && (
              <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400">Buy:</span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {sitePrices
                      ? `${(sitePrices.current.import * 100).toFixed(0)}c`
                      : nemPrices
                        ? `${(nemPrices.current.price_kwh * 100).toFixed(0)}c`
                        : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400">Sell:</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {sitePrices
                      ? `${(sitePrices.current.export * 100).toFixed(0)}c`
                      : nemPrices
                        ? `${((nemPrices.current.price_kwh * 0.5) * 100).toFixed(0)}c`
                        : '-'}
                  </span>
                </div>
              </div>
            )}
            {/* Live indicator */}
            {isLiveData ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                Demo Data
                {telemetryError && (
                  <span className="ml-1 text-xs" title={telemetryError}>⚠️</span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Stacked Gauges Column */}
          <div className="flex flex-col gap-1">
            {/* Solar Generation Gauge */}
            <div style={{ paddingTop: '10px' }}>
              <HorseshoeGauge
                value={liveData.solar.power_w}
                max={9000}
                unit="W"
                label="Solar"
                colorStops={solarColorStops}
                size="md"
                badge={
                  liveData.solar.curtailment?.active
                    ? liveData.solar.curtailment.mode === 'applied'
                      ? { text: 'Curtailed', variant: 'solid' }
                      : { text: 'Curtail plan', variant: 'outline' }
                    : undefined
                }
              />
            </div>
            {/* Current Load Gauge */}
            <div style={{ paddingTop: '25px' }}>
              <HorseshoeGauge
                value={liveData.load.power_w}
                max={6000}
                unit="W"
                label="Load"
                colorStops={loadColorStops}
                size="md"
              />
            </div>
          </div>

          {/* Solar Forecast */}
          <div className="md:col-span-2 relative">
            {isAdmin && (
              <button
                onClick={handleRefreshForecast}
                disabled={refreshingForecast}
                className="absolute top-3 right-3 z-10 p-1.5 rounded-lg bg-card/80 dark:bg-gray-800/80 hover:bg-card dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors disabled:opacity-50"
                title="Refresh Forecast"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-brand-orange ${refreshingForecast ? 'animate-spin' : ''}`} />
              </button>
            )}
            <SolarForecastCard forecast={displayedForecast ?? liveData.forecast} compact />
          </div>

          {/* Power Flow Visualization */}
          <div className="md:col-span-2 lg:col-span-2">
            <PowerFlowCard data={liveData} ev={evStatus} />
          </div>

          {/* Cost Totals */}
          <div className="md:col-span-1 lg:col-span-1">
            <div className="card p-4 h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Energy Costs</h3>
                {siteId && (
                  <Link
                    to={`/site/${siteId}/energy-history`}
                    className="inline-flex items-center gap-1 text-xs text-brand-orange hover:underline"
                    title="View energy history"
                  >
                    History
                  </Link>
                )}
              </div>
              <div className="space-y-3">
                {/* Today */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Today</span>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${(costTotals.today?.net_cost ?? 0) >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {costTotals.today ? (costTotals.today.net_cost >= 0 ? '-' : '+') : ''}
                      ${Math.abs(costTotals.today?.net_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                {/* Last 7 days */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Last 7 Days</span>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${(costTotals.week?.net_cost ?? 0) >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {costTotals.week ? (costTotals.week.net_cost >= 0 ? '-' : '+') : ''}
                      ${Math.abs(costTotals.week?.net_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                {/* Last 30 days */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Last 30 Days</span>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${(costTotals.month?.net_cost ?? 0) >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {costTotals.month ? (costTotals.month.net_cost >= 0 ? '-' : '+') : ''}
                      ${Math.abs(costTotals.month?.net_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                {/* Details */}
                {costTotals.today && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>Import: {costTotals.today.total_import_kwh.toFixed(1)} kWh (${costTotals.today.total_import_cost.toFixed(2)})</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>Export: {costTotals.today.total_export_kwh.toFixed(1)} kWh (${costTotals.today.total_export_value.toFixed(2)})</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Battery Status and Charts Row */}
      {liveData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Battery Status Card */}
          <div className="lg:col-span-1">
            <BatteryStatusCard data={liveData} siteId={siteId!} isAdmin={isAdmin} />
          </div>

          {/* Energy Flow Chart */}
          <div className="lg:col-span-2">
            <EnergyFlowChart
              data={energyFlowData}
              historyHref={siteId ? `/site/${siteId}/energy-history` : undefined}
            />
          </div>
        </div>
      )}

      {/* EV Chargers — full card per provisioned charger + "Add EV Charger"
          provisioning flow. Renders nothing while the charger list loads. */}
      <EVChargersSection siteId={site.id} />

      {/* Prices - show different display based on provider.
          GloBird ZeroHero is a fixed-tariff plan with no live price feed, so the
          price section is hidden entirely for it. */}
      {site?.price_provider === 'globird_zerohero' ? null : site?.price_provider === 'flowpower_happy_hour' ? (
        <HappyHourCard
          prices={sitePrices}
          window={resolveHappyHourWindow(currentSiteCreds?.price_provider_credentials)}
        />
      ) : site?.price_provider === 'amber' ? (
        // Amber prices - show site-specific tariffs with colored forecast
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">💡</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Amber Electric Prices
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                {site.region}
              </span>
            </div>
            {sitePrices && (
              <span className="text-xs text-gray-400">
                Updated {new Date(sitePrices.timestamp).toLocaleTimeString('en-AU')}
              </span>
            )}
          </div>

          {sitePricesLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange"></div>
            </div>
          ) : sitePrices ? (
            <div className="space-y-4">
              {/* Current prices with forecast underneath */}
              <div className="grid grid-cols-2 gap-4">
                {/* Import (Buy) Column */}
                <div>
                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/30 rounded-lg mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Import (Buy)</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {(sitePrices.current.import * 100).toFixed(1)}
                      <span className="text-sm font-normal">c/kWh</span>
                    </p>
                  </div>
                  {/* Import forecast - next 4 hours (8 x 30min intervals) */}
                  {sitePrices.forecast && sitePrices.forecast.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {sitePrices.forecast.slice(0, 8).map((f, idx) => {
                        const priceKwh = f.import;
                        const bgColor = priceKwh < 0 ? 'bg-green-500' :
                          priceKwh < 0.10 ? 'bg-green-400' :
                          priceKwh < 0.20 ? 'bg-emerald-400' :
                          priceKwh < 0.30 ? 'bg-yellow-400' :
                          priceKwh < 0.40 ? 'bg-orange-400' :
                          priceKwh < 0.50 ? 'bg-red-400' : 'bg-red-600';
                        return (
                          <div
                            key={`import-${idx}`}
                            className={`flex-shrink-0 flex flex-col items-center p-1.5 rounded-md ${bgColor} min-w-[44px]`}
                          >
                            <span className="text-[9px] text-white/70 font-medium">
                              {new Date(f.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                            <span className="text-xs text-white font-bold">
                              {(f.import * 100).toFixed(0)}c
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Export (Sell) Column */}
                <div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/30 rounded-lg mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Export (Sell)</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {(sitePrices.current.export * 100).toFixed(1)}
                      <span className="text-sm font-normal">c/kWh</span>
                    </p>
                  </div>
                  {/* Export forecast - next 4 hours (8 x 30min intervals) */}
                  {sitePrices.forecast && sitePrices.forecast.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {sitePrices.forecast.slice(0, 8).map((f, idx) => {
                        const priceKwh = f.export;
                        const bgColor = priceKwh < 0 ? 'bg-green-500' :
                          priceKwh < 0.03 ? 'bg-red-400' :
                          priceKwh < 0.05 ? 'bg-orange-400' :
                          priceKwh < 0.08 ? 'bg-yellow-400' :
                          priceKwh < 0.12 ? 'bg-emerald-400' : 'bg-green-400';
                        return (
                          <div
                            key={`export-${idx}`}
                            className={`flex-shrink-0 flex flex-col items-center p-1.5 rounded-md ${bgColor} min-w-[44px]`}
                          >
                            <span className="text-[9px] text-white/70 font-medium">
                              {new Date(f.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                            <span className="text-xs text-white font-bold">
                              {(f.export * 100).toFixed(0)}c
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No price data available. Price poller may not have run yet.
            </p>
          )}
        </div>
      ) : site?.price_provider === 'simulated' ? (
        // Simulated prices - show simplified buy/sell table
        <SimulatedPricesCard
          prices={sitePrices}
          loading={sitePricesLoading}
          isAdmin={isAdmin}
          onRefresh={handleRefreshPrices}
          refreshing={refreshingPrices}
        />
      ) : (
        // AEMO or no provider - show NEM wholesale prices
        <PriceBlocksCard
          prices={nemPrices}
          loading={nemPricesLoading}
          error={nemPricesError}
        />
      )}

      {/* Battery Plan Chart - Full Width */}
      {batteryPlanData.length > 0 && (
        <div className="relative">
          {isAdmin && (
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={() => handleTriggerOptimization('mpc')}
                disabled={triggeringOptimization !== null}
                className="p-2 rounded-lg bg-card/80 dark:bg-gray-800/80 hover:bg-card dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors disabled:opacity-50"
                title="Run MPC Optimization"
              >
                {triggeringOptimization === 'mpc' ? (
                  <Loader2 className="w-4 h-4 animate-spin text-brand-orange" />
                ) : (
                  <Zap className="w-4 h-4 text-brand-orange" />
                )}
              </button>
              <button
                onClick={() => handleTriggerOptimization('dayahead')}
                disabled={triggeringOptimization !== null}
                className="p-2 rounded-lg bg-card/80 dark:bg-gray-800/80 hover:bg-card dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors disabled:opacity-50"
                title="Run Day-Ahead Optimization"
              >
                {triggeringOptimization === 'dayahead' ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                ) : (
                  <Calendar className="w-4 h-4 text-blue-500" />
                )}
              </button>
              <button
                onClick={handleExportDiagnostics}
                disabled={exportingDiagnostics}
                className="p-2 rounded-lg bg-card/80 dark:bg-gray-800/80 hover:bg-card dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors disabled:opacity-50"
                title="Export diagnostics (admin) — site config, cached optimizer plan, prices/forecast, mode events, intervals"
              >
                {exportingDiagnostics ? (
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                ) : (
                  <Download className="w-4 h-4 text-purple-500" />
                )}
              </button>
            </div>
          )}
          <BatteryPlanChart
            data={batteryPlanData}
            optimizationMode={
              (site?.optimizer_config?.optimization_mode as string | undefined) ||
              'balanced'
            }
            ratedDischargeW={site?.battery_config?.max_discharge_w}
            priceProvider={site?.price_provider}
            region={site?.region}
            freeChargeWindow={
              site?.price_provider === 'globird_zerohero'
                ? (() => {
                    const w = resolveFreeWindow(currentSiteCreds?.price_provider_credentials);
                    return { startHour: w.startHour, endHour: w.endHour };
                  })()
                : undefined
            }
            happyHourWindow={
              site?.price_provider === 'flowpower_happy_hour'
                ? resolveHappyHourWindow(currentSiteCreds?.price_provider_credentials)
                : undefined
            }
          />
        </div>
      )}

      {/* Provider Configuration */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuration
          </h2>
          <button
            onClick={openSiteEditModal}
            className="flex items-center gap-1 text-sm text-brand-orange hover:underline"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        </div>
        <div className={`grid grid-cols-2 gap-4 ${site.solar_control_adapter ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          <ConfigItem
            label="Price Provider"
            value={priceProviderLabel(site.price_provider)}
            status={
              providerStatus?.price_provider?.status === 'connected' ? 'connected' :
              providerStatus?.price_provider?.status === 'error' ? 'error' :
              providerStatus?.price_provider?.status === 'pending' ? 'configured' :
              providerStatus?.price_provider?.status === 'not_configured' ? 'not_configured' :
              !site.price_provider ? 'not_configured' :
              // Keyless / static-tariff providers need no credentials to be "configured".
              // (GloBird ZeroHero prices are a fixed TOU structure resolved from the tariff library.)
              site.price_provider === 'aemo' || site.price_provider === 'simulated' ||
              site.price_provider === 'globird_zerohero' ||
              site.price_provider === 'flowpower_happy_hour' ? 'configured' :
              site.price_provider_credentials ? 'configured' : 'not_configured'
            }
          />
          <ConfigItem
            label="Forecast Provider"
            value={forecastProviderLabel(site.forecast_provider)}
            status={
              providerStatus?.forecast_provider?.status === 'connected' ? 'connected' :
              providerStatus?.forecast_provider?.status === 'error' ? 'error' :
              providerStatus?.forecast_provider?.status === 'pending' ? 'configured' :
              providerStatus?.forecast_provider?.status === 'not_configured' ? 'not_configured' :
              !site.forecast_provider ? 'not_configured' :
              // SolanIQ Forecaster (open_meteo) is keyless: it just needs the
              // site's location + system size to produce a forecast.
              site.forecast_provider === 'open_meteo' ?
                (site.latitude != null && site.longitude != null && Boolean(site.solar_config?.capacity_kw)
                  ? 'configured' : 'not_configured') :
              // forecast_solar and simulated are keyless too
              site.forecast_provider === 'forecast_solar' || site.forecast_provider === 'simulated' ? 'configured' :
              site.forecast_provider_credentials ? 'configured' : 'not_configured'
            }
          />
          <ConfigItem
            label="Optimizer Engine"
            value={
              site.optimizer_engine
                ? OPTIMIZER_ENGINES.find((e) => e.value === site.optimizer_engine)?.label
                  ?? site.optimizer_engine
                : 'Not set'
            }
            status={site.optimizer_engine ? 'configured' : 'not_configured'}
          />
          <ConfigItem
            label="Battery Adapter"
            value={site.battery_adapter || 'Not set'}
            status={
              providerStatus?.battery_adapter?.status === 'connected' ? 'connected' :
              providerStatus?.battery_adapter?.status === 'error' ? 'error' :
              !site.battery_adapter ? 'not_configured' :
              site.battery_adapter === 'simulated' ? 'configured' :
              site.battery_credentials ? 'configured' : 'not_configured'
            }
          />
          {/* Solar inverter control — only shown when one is selected. */}
          {site.solar_control_adapter && (
            <ConfigItem
              label="Solar Inverter"
              value={
                SOLAR_INVERTERS.find((i) => i.value === site.solar_control_adapter)?.label
                  ?? site.solar_control_adapter
              }
              status={
                providerStatus?.solar_control_adapter?.status === 'connected' ? 'connected' :
                providerStatus?.solar_control_adapter?.status === 'error' ? 'error' :
                providerStatus?.solar_control_adapter?.status === 'pending' ? 'configured' :
                providerStatus?.solar_control_adapter?.status === 'not_configured' ? 'not_configured' :
                'configured'
              }
            />
          )}
        </div>
      </div>

      {/* Battery Specifications */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Battery className="w-5 h-5" />
            Battery Specifications
          </h2>
          <button
            onClick={openSiteEditModal}
            className="flex items-center gap-1 text-sm text-brand-orange hover:underline"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ConfigItem label="Capacity" value={`${((site.battery_config?.capacity_wh as number) / 1000 || 0).toFixed(1)} kWh`} />
          <ConfigItem label="Max Charge Rate" value={`${((site.battery_config?.max_charge_w as number) / 1000 || 0).toFixed(1)} kW`} />
          <ConfigItem label="Max Discharge Rate" value={`${((site.battery_config?.max_discharge_w as number) / 1000 || 0).toFixed(1)} kW`} />
          {isAdmin && site.battery_config?.max_export_w ? (
            <ConfigItem label="Grid Export Cap" value={`${((site.battery_config.max_export_w as number) / 1000).toFixed(1)} kW`} />
          ) : null}
        </div>
      </div>

      {/* Site Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Site Information
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Site ID</p>
            <p className="font-mono text-xs text-gray-900 dark:text-white truncate">{site.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {new Date(site.created_at).toLocaleDateString('en-AU')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {new Date(site.updated_at).toLocaleDateString('en-AU')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
            <p className={`font-medium ${site.active ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
              {site.active ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
      </div>

      {/* Performance Metrics — admin-only; uninteresting to regular users */}
      {isAdmin && (
        <div className="card p-6">
          <PerformanceMetrics siteId={site.id} refreshInterval={30000} />
        </div>
      )}

      {/* Site Edit Modal */}
      {showSiteModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="card shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Site</h2>
              <button onClick={() => setShowSiteModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto"
              autoComplete="off"
              onSubmit={(e) => e.preventDefault()}
            >
              {saveError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {saveError}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Site Name</label>
                <input
                  type="text"
                  value={siteFormData.name || ''}
                  onChange={(e) => setSiteFormData({ ...siteFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                />
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Region</label>
                <select
                  value={siteFormData.region || ''}
                  onChange={(e) => setSiteFormData({ ...siteFormData, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                >
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* Optimizer Engine - now visible (was buried under Advanced) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Optimizer Engine</label>
                <select
                  value={siteFormData.optimizer_engine || ''}
                  onChange={(e) => setSiteFormData({ ...siteFormData, optimizer_engine: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                >
                  {OPTIMIZER_ENGINES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>

              {/* Optimizer tuning — only shown for the EMHASS engine. */}
              {siteFormData.optimizer_engine === 'emhass' && (
                <>
                  <SectionHeader
                    title="Optimizer tuning (advanced)"
                    icon={<Cpu className="w-4 h-4" />}
                    isOpen={openSiteSection === 'optimizer'}
                    onToggle={() =>
                      setOpenSiteSection(openSiteSection === 'optimizer' ? null : 'optimizer')
                    }
                  />
                  {openSiteSection === 'optimizer' && (
                    <OptimizerTuningFields
                      value={(siteFormData.optimizer_config as Record<string, unknown>) || {}}
                      onChange={(next) =>
                        setSiteFormData({ ...siteFormData, optimizer_config: next })
                      }
                    />
                  )}
                </>
              )}

              {/* Site Active checkbox - now visible */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="site-active"
                  checked={siteFormData.active || false}
                  onChange={(e) => setSiteFormData({ ...siteFormData, active: e.target.checked })}
                  className="w-4 h-4 text-brand-orange border-gray-300 rounded"
                />
                <label htmlFor="site-active" className="text-sm text-gray-700 dark:text-gray-300">Site is active</label>
              </div>

              {/* ============= Battery section ============= */}
              <SectionHeader
                title="Battery"
                icon={<BatteryIcon className="w-4 h-4" />}
                isOpen={openSiteSection === 'battery'}
                onToggle={() => setOpenSiteSection(openSiteSection === 'battery' ? null : 'battery')}
              />
              {openSiteSection === 'battery' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Battery System</label>
                    <select
                      value={siteFormData.battery_adapter || ''}
                      onChange={(e) =>
                        setSiteFormData({ ...siteFormData, battery_adapter: e.target.value, battery_credentials: {} })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {BATTERY_ADAPTERS.map((b) => (
                        <option key={b.value} value={b.value} disabled={b.comingSoon}>
                          {b.label}{b.comingSoon ? ' (Coming Soon)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {siteFormData.battery_adapter && BATTERY_ADAPTER_FIELDS[siteFormData.battery_adapter]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Connection Settings <span>(leave blank to keep existing)</span>
                      </p>
                      {BATTERY_ADAPTER_FIELDS[siteFormData.battery_adapter].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="battery"
                          value={(siteFormData.battery_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setSiteFormData({
                              ...siteFormData,
                              battery_credentials: { ...siteFormData.battery_credentials, [field.key]: value },
                            })
                          }
                          editing={true}
                          revealed={revealedSiteFields.has(`battery-${field.key}`)}
                          onToggleReveal={() => toggleRevealedSite(`battery-${field.key}`)}
                          credsLoading={siteCredsLoading}
                          currentValue={getCurrentSiteCredValue('battery', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Storage Capacity (kWh)</label>
                    <input
                      type="number"
                      value={(siteFormData.battery_config?.capacity_wh as number) / 1000 || ''}
                      onChange={(e) =>
                        setSiteFormData({
                          ...siteFormData,
                          battery_config: { ...siteFormData.battery_config, capacity_wh: parseFloat(e.target.value) * 1000 },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                  </div>

                  {/* Operator-only tuning: export cap and the experimental SOC-floor EV hold */}
                  {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grid Export Cap (kW, optional)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={(siteFormData.battery_config?.max_export_w as number) / 1000 || ''}
                      onChange={(e) => {
                        const kw = parseFloat(e.target.value);
                        const { max_export_w: _omit, ...rest } = siteFormData.battery_config || {};
                        setSiteFormData({
                          ...siteFormData,
                          battery_config: kw > 0 ? { ...rest, max_export_w: kw * 1000 } : rest,
                        });
                      }}
                      placeholder="Leave blank if the site can export at the battery's full rate"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      The most this site can send to the grid (DNSP or inverter limit). The optimiser plans battery sales at this rate instead of the battery's discharge rating.
                    </p>
                  </div>
                  )}

                  {isAdmin && (
                  <div>
                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={Boolean(siteFormData.battery_config?.ev_hold_via_soc_floor)}
                        onChange={(e) => {
                          const { ev_hold_via_soc_floor: _omit, ...rest } = siteFormData.battery_config || {};
                          setSiteFormData({
                            ...siteFormData,
                            battery_config: e.target.checked ? { ...rest, ev_hold_via_soc_floor: true } : rest,
                          });
                        }}
                      />
                      <span>
                        <span className="font-medium">Hold the home battery with its SOC floor while the EV charges</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Experimental. While an EV is drawing and the plan does not want the battery to discharge, keep Regular Mode and raise the
                          inverter's on-grid discharge floor to the current SOC, instead of switching to Charge Mode (which can charge the battery
                          from the grid). Untick to go back to the Charge Mode hold.
                        </span>
                      </span>
                    </label>
                  </div>
                  )}
                </div>
              )}

              {/* ============= Solar section (forecast provider) ============= */}
              <SectionHeader
                title="Solar"
                icon={<SunIcon className="w-4 h-4" />}
                isOpen={openSiteSection === 'solar'}
                onToggle={() => setOpenSiteSection(openSiteSection === 'solar' ? null : 'solar')}
              />
              {openSiteSection === 'solar' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Forecast Provider</label>
                    {/* Forecasting now uses the built-in SolanIQ Forecaster by default, so the
                        provider is shown read-only. Existing Solcast sites keep their provider
                        (and their credential fields below) untouched. */}
                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white">
                      {forecastProviderLabel(siteFormData.forecast_provider || 'open_meteo')}
                    </div>
                    {(!siteFormData.forecast_provider || siteFormData.forecast_provider === 'open_meteo') && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        SolanIQ Forecaster generates the solar forecast automatically from this
                        site's location and system size — no setup needed.
                      </p>
                    )}
                  </div>

                  {siteFormData.forecast_provider && FORECAST_PROVIDER_FIELDS[siteFormData.forecast_provider]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Credentials <span>(leave blank to keep existing)</span>
                      </p>
                      {FORECAST_PROVIDER_FIELDS[siteFormData.forecast_provider].map((field) => {
                        // Solcast resource_ids is a special multi-select widget — skip the
                        // generic CredentialField path for it.
                        if (
                          field.type === 'multiselect' &&
                          field.key === 'resource_ids' &&
                          siteFormData.forecast_provider === 'solcast'
                        ) {
                          return (
                            <div key={field.key}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                              </label>
                              <div className="space-y-2">
                                <div className="flex gap-2 items-start">
                                  <div className="flex-1 space-y-1">
                                    {solcastResources.length === 0 ? (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                        Click "Fetch Resources" to load your Solcast rooftop sites
                                      </p>
                                    ) : (
                                      <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-card dark:bg-gray-700">
                                        {solcastResources.map((resource) => {
                                          const selectedIds = (siteFormData.forecast_provider_credentials?.resource_ids as string[]) || [];
                                          const isSelected = selectedIds.includes(resource.resource_id);
                                          return (
                                            <label
                                              key={resource.resource_id}
                                              className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => {
                                                  const newIds = e.target.checked
                                                    ? [...selectedIds, resource.resource_id]
                                                    : selectedIds.filter(id => id !== resource.resource_id);
                                                  setSiteFormData({
                                                    ...siteFormData,
                                                    forecast_provider_credentials: {
                                                      ...siteFormData.forecast_provider_credentials,
                                                      resource_ids: newIds,
                                                    },
                                                  });
                                                }}
                                                className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                                              />
                                              <div className="flex-1">
                                                <span className="text-sm text-gray-900 dark:text-white">{resource.name}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                                  {resource.capacity_kw}kW • {resource.tilt}° tilt • {resource.azimuth}° azimuth
                                                </span>
                                              </div>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleFetchSolcastResources}
                                    disabled={loadingSolcastResources || !(siteFormData.forecast_provider_credentials?.api_key)}
                                    className="px-3 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange/90 rounded-full disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {loadingSolcastResources ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4" />
                                    )}
                                    Fetch Resources
                                  </button>
                                </div>
                                {solcastResourcesError && (
                                  <p className="text-xs text-red-500">{solcastResourcesError}</p>
                                )}
                                {solcastResources.length > 0 && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Found {solcastResources.length} resource{solcastResources.length !== 1 ? 's' : ''} •
                                    {((siteFormData.forecast_provider_credentials?.resource_ids as string[]) || []).length} selected
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        }
                        // Regular credential input.
                        return (
                          <CredentialField
                            key={field.key}
                            field={field}
                            formNamespace="forecast"
                            value={(siteFormData.forecast_provider_credentials?.[field.key] as string) || ''}
                            onChange={(value) =>
                              setSiteFormData({
                                ...siteFormData,
                                forecast_provider_credentials: {
                                  ...siteFormData.forecast_provider_credentials,
                                  [field.key]: value,
                                },
                              })
                            }
                            editing={true}
                            revealed={revealedSiteFields.has(`forecast-${field.key}`)}
                            onToggleReveal={() => toggleRevealedSite(`forecast-${field.key}`)}
                            credsLoading={siteCredsLoading}
                            currentValue={getCurrentSiteCredValue('forecast', field.key)}
                            formatCredValue={formatCredValue}
                          />
                        );
                      })}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Solar Inverter Control</label>
                    <select
                      value={siteFormData.solar_control_adapter || ''}
                      onChange={(e) =>
                        setSiteFormData({ ...siteFormData, solar_control_adapter: e.target.value, solar_credentials: {} })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {SOLAR_INVERTERS.map((inv) => (
                        <option key={inv.value} value={inv.value} disabled={inv.comingSoon}>
                          {inv.label}{inv.comingSoon ? ' (Coming Soon)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Lets SolanIQ curtail solar production when the plan calls for it
                      (e.g. negative feed-in prices with a full battery).
                    </p>
                  </div>

                  {siteFormData.solar_control_adapter && SOLAR_CONTROL_FIELDS[siteFormData.solar_control_adapter]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Credentials <span>(leave blank to keep existing)</span>
                      </p>
                      {SOLAR_CONTROL_FIELDS[siteFormData.solar_control_adapter].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="solar"
                          value={(siteFormData.solar_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setSiteFormData({
                              ...siteFormData,
                              solar_credentials: { ...siteFormData.solar_credentials, [field.key]: value },
                            })
                          }
                          editing={true}
                          revealed={revealedSiteFields.has(`solar-${field.key}`)}
                          onToggleReveal={() => toggleRevealedSite(`solar-${field.key}`)}
                          credsLoading={siteCredsLoading}
                          currentValue={getCurrentSiteCredValue('solar', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ============= Price section ============= */}
              <SectionHeader
                title="Price"
                icon={<DollarSignIcon className="w-4 h-4" />}
                isOpen={openSiteSection === 'price'}
                onToggle={() => setOpenSiteSection(openSiteSection === 'price' ? null : 'price')}
              />
              {openSiteSection === 'price' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price Provider</label>
                    <select
                      value={siteFormData.price_provider || ''}
                      onChange={(e) =>
                        setSiteFormData({
                          ...siteFormData,
                          price_provider: e.target.value,
                          price_provider_credentials: {},
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {PRICE_PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {priceProviderLabel(p)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {siteFormData.price_provider === 'flowpower_happy_hour' && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                      <FlowPowerPlanFields
                        key={site?.id}
                        value={flowpowerForm}
                        onChange={(form) => {
                          setFlowpowerForm(form);
                          setSiteFormData((prev) => ({
                            ...prev,
                            price_provider_credentials: flowPowerCredentials(
                              form,
                              prev.region ?? site?.region,
                            ),
                          }));
                        }}
                        region={siteFormData.region ?? site?.region}
                      />
                    </div>
                  )}

                  {siteFormData.price_provider && PRICE_PROVIDER_FIELDS[siteFormData.price_provider]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Credentials <span>(leave blank to keep existing)</span>
                      </p>
                      {PRICE_PROVIDER_FIELDS[siteFormData.price_provider].map((field) => {
                        // Amber site_id is a special select populated by Fetch Sites — skip
                        // the generic CredentialField path for it.
                        if (
                          field.type === 'select' &&
                          field.key === 'site_id' &&
                          siteFormData.price_provider === 'amber'
                        ) {
                          return (
                            <div key={field.key}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                              </label>
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <select
                                    value={(siteFormData.price_provider_credentials?.site_id as string) || ''}
                                    onChange={(e) =>
                                      setSiteFormData({
                                        ...siteFormData,
                                        price_provider_credentials: {
                                          ...siteFormData.price_provider_credentials,
                                          site_id: e.target.value,
                                        },
                                      })
                                    }
                                    disabled={amberSites.length === 0}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent disabled:opacity-50"
                                  >
                                    <option value="">
                                      {amberSites.length === 0 ? 'Click "Fetch Sites" first' : 'Select a site...'}
                                    </option>
                                    {amberSites.map((s) => (
                                      <option key={s.id} value={s.id}>
                                        {s.nmi} ({s.network}) - {s.status}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={handleFetchAmberSites}
                                    disabled={loadingAmberSites || !(siteFormData.price_provider_credentials?.api_key)}
                                    className="px-3 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange/90 rounded-full disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {loadingAmberSites ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4" />
                                    )}
                                    Fetch Sites
                                  </button>
                                </div>
                                {amberSitesError && (
                                  <p className="text-xs text-red-500">{amberSitesError}</p>
                                )}
                                {amberSites.length > 0 && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Found {amberSites.length} site{amberSites.length !== 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        }
                        // GloBird tariff_id: a dropdown of CDR tariffs matched to
                        // the site's postcode (may list >1 distributor at a
                        // network boundary) instead of a raw id text field.
                        if (
                          field.key === 'tariff_id' &&
                          siteFormData.price_provider === 'globird_zerohero'
                        ) {
                          return (
                            <div key={field.key}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Distributor Tariff {field.required && <span className="text-red-500">*</span>}
                              </label>
                              <select
                                value={(siteFormData.price_provider_credentials?.tariff_id as string) || ''}
                                onChange={(e) =>
                                  setSiteFormData({
                                    ...siteFormData,
                                    price_provider_credentials: {
                                      ...siteFormData.price_provider_credentials,
                                      tariff_id: e.target.value,
                                    },
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                              >
                                <option value="">
                                  {globirdTariffs.length === 0
                                    ? 'No tariffs found for this site'
                                    : 'Select your distributor tariff'}
                                </option>
                                {globirdTariffs.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.distributor} — {t.name}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Matched to the site's postcode. Usage rate comes from this
                                tariff; the ZeroHero feed-in windows are applied automatically.
                              </p>
                            </div>
                          );
                        }
                        // GloBird free charging (ZeroCharge) window: one select
                        // that sets both free_window_start and free_window_end.
                        // GloBird has moved this window before, so it's a plan
                        // setting rather than a hard-coded constant.
                        if (
                          field.key === 'free_window_start' &&
                          siteFormData.price_provider === 'globird_zerohero'
                        ) {
                          const selected = resolveFreeWindow(siteFormData.price_provider_credentials);
                          return (
                            <div key={field.key}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Free Charging Window
                              </label>
                              <select
                                value={`${selected.start}-${selected.end}`}
                                onChange={(e) => {
                                  const opt = GLOBIRD_FREE_WINDOW_OPTIONS.find(
                                    (o) => `${o.start}-${o.end}` === e.target.value,
                                  );
                                  if (!opt) return;
                                  setSiteFormData({
                                    ...siteFormData,
                                    price_provider_credentials: {
                                      ...siteFormData.price_provider_credentials,
                                      free_window_start: opt.start,
                                      free_window_end: opt.end,
                                    },
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                              >
                                {GLOBIRD_FREE_WINDOW_OPTIONS.map((o) => (
                                  <option key={`${o.start}-${o.end}`} value={`${o.start}-${o.end}`}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                The plan's free grid-charging window. Match this to the
                                window on your GloBird plan documents.
                              </p>
                            </div>
                          );
                        }

                        return (
                          <CredentialField
                            key={field.key}
                            field={field}
                            formNamespace="price"
                            value={(siteFormData.price_provider_credentials?.[field.key] as string) || ''}
                            onChange={(value) =>
                              setSiteFormData({
                                ...siteFormData,
                                price_provider_credentials: {
                                  ...siteFormData.price_provider_credentials,
                                  [field.key]: value,
                                },
                              })
                            }
                            editing={true}
                            revealed={revealedSiteFields.has(`price-${field.key}`)}
                            onToggleReveal={() => toggleRevealedSite(`price-${field.key}`)}
                            credsLoading={siteCredsLoading}
                            currentValue={getCurrentSiteCredValue('price', field.key)}
                            formatCredValue={formatCredValue}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Simulation Scenario (shown when any provider is simulated) */}
                  {(siteFormData.price_provider === 'simulated' ||
                    siteFormData.forecast_provider === 'simulated' ||
                    siteFormData.battery_adapter === 'simulated') && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Simulation Mode Active</span>
                      </div>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        This site uses simulated providers for testing. Select a scenario to control the simulation behavior.
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Simulation Scenario
                        </label>
                        <select
                          value={siteFormData.simulation_config?.scenario || 'sunny_day_tou'}
                          onChange={(e) =>
                            setSiteFormData({
                              ...siteFormData,
                              simulation_config: { ...siteFormData.simulation_config, scenario: e.target.value },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        >
                          {SIMULATION_SCENARIOS.map((scenario) => (
                            <option key={scenario.id} value={scenario.id}>
                              {scenario.name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {SIMULATION_SCENARIOS.find((s) => s.id === (siteFormData.simulation_config?.scenario || 'sunny_day_tou'))?.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </form>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowSiteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSite}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

// Status indicator types
type ServiceStatus = 'connected' | 'configured' | 'error' | 'not_configured';

function StatusIndicator({ status }: { status: ServiceStatus }) {
  const config = {
    connected: { color: 'bg-green-500', pulse: true, label: 'Connected' },
    configured: { color: 'bg-blue-500', pulse: false, label: 'Configured' },
    error: { color: 'bg-red-500', pulse: false, label: 'Error' },
    not_configured: { color: 'bg-gray-400', pulse: false, label: 'Not Configured' },
  };
  const { color, pulse, label } = config[status];

  return (
    <span className="flex items-center gap-1.5" title={label}>
      <span className={`w-2 h-2 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`}></span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </span>
  );
}

function ConfigItem({ label, value, status }: { label: string; value: string; status?: ServiceStatus }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="font-medium text-gray-900 dark:text-white capitalize">{value.replace('_', ' ')}</p>
      {status && <StatusIndicator status={status} />}
    </div>
  );
}

interface OptimizerTuningFieldsProps {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

/**
 * Editor for the SolanIQ Optimizer (EMHASS) tuning knobs that live in
 * `site.optimizer_config`. Backend defaults are in
 * `shared/adapters/optimizer/emhass.py::DEFAULT_OPTIMIZER_CONFIG`. Anything left
 * blank here falls through to the preset and then the defaults — we only
 * persist explicit overrides.
 */
function OptimizerTuningFields({ value, onChange }: OptimizerTuningFieldsProps) {
  const mode = (value.optimization_mode as string) || 'balanced';
  const socMin = value.battery_minimum_state_of_charge as number | undefined;
  const socMax = value.battery_maximum_state_of_charge as number | undefined;

  const update = (patch: Record<string, unknown>) => {
    const next = { ...value, ...patch };
    // Clean up keys that have been reset to undefined so they don't end up
    // as explicit `null`s in the JSONB column.
    Object.keys(patch).forEach((k) => {
      if (next[k] === undefined || next[k] === '' || Number.isNaN(next[k] as number)) {
        delete next[k];
      }
    });
    onChange(next);
  };

  const parsePercent = (raw: string): number | undefined => {
    if (raw === '') return undefined;
    const n = Number.parseFloat(raw);
    if (Number.isNaN(n)) return undefined;
    // Clamp to a sensible 0-100% range; persist as a 0-1 fraction.
    return Math.max(0, Math.min(100, n)) / 100;
  };

  const fmtPercent = (v: number | undefined, fallback: number) =>
    ((v ?? fallback) * 100).toFixed(0);

  return (
    <div className="space-y-4 px-3 py-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/40">
      {/* Aggressiveness preset */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Aggressiveness
        </label>
        <div className="space-y-1.5">
          {EMHASS_MODE_PRESETS.map((preset) => (
            <label
              key={preset.value}
              className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                mode === preset.value
                  ? 'border-brand-orange bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-card dark:hover:bg-gray-800'
              }`}
            >
              <input
                type="radio"
                name="optimization_mode"
                value={preset.value}
                checked={mode === preset.value}
                onChange={() => update({ optimization_mode: preset.value })}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {preset.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {preset.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* SOC bounds */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            SOC min (%)
          </label>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            placeholder={`${fmtPercent(undefined, EMHASS_SOC_DEFAULTS.battery_minimum_state_of_charge)} (default)`}
            value={socMin === undefined ? '' : Math.round(socMin * 100)}
            onChange={(e) =>
              update({ battery_minimum_state_of_charge: parsePercent(e.target.value) })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange"
          />
          <p className="mt-1 text-[11px] text-gray-400">Lower = deeper cycling.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            SOC max (%)
          </label>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            placeholder={`${fmtPercent(undefined, EMHASS_SOC_DEFAULTS.battery_maximum_state_of_charge)} (default)`}
            value={socMax === undefined ? '' : Math.round(socMax * 100)}
            onChange={(e) =>
              update({ battery_maximum_state_of_charge: parsePercent(e.target.value) })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange"
          />
          <p className="mt-1 text-[11px] text-gray-400">Higher = more storage headroom.</p>
        </div>
      </div>

      {socMin !== undefined && socMax !== undefined && socMin >= socMax && (
        <p className="text-xs text-red-600 dark:text-red-400">
          SOC min must be lower than SOC max.
        </p>
      )}
    </div>
  );
}
